import React, { useState } from 'react';
import { YStack, XStack, SizableText, H3, H4, Card, Separator, Button } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { View, Dimensions, Platform, Clipboard, Alert, ScrollView, Pressable } from 'react-native';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import ErrorBoundary from '@utils/ErrorBoundary';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useNavigation } from '@react-navigation/native';

const LovableOptions = () => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text);
    } else {
      Clipboard.setString(text);
    }
    setCopiedItem(label);
    setTimeout(() => setCopiedItem(null), 2000);
    Alert.alert('Copied', `${label} copied to clipboard`);
  };

  const navigateToAPI = () => {
    (navigation as any).navigate('Settings', {
      screen: 'API',
    });
  };

  const apiEndpoint = 'https://api.smartsaas.pro/api/v1';
  const deploymentCodeExample = `// Lovable Deployment Hook Example
// Add this to your Lovable project's deployment configuration

const postDeploymentToSmartSaaS = async (deploymentInfo) => {
  const response = await fetch('${apiEndpoint}/integrations/lovable/deployment', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer YOUR_API_KEY_HERE',
    },
    body: JSON.stringify({
      deployment: {
        url: deploymentInfo.url,
        version: deploymentInfo.version || '1.0.0',
        timestamp: new Date().toISOString(),
        environment: deploymentInfo.environment || 'production',
        commitHash: deploymentInfo.commitHash,
        branch: deploymentInfo.branch,
      },
      app: {
        name: deploymentInfo.appName,
        description: deploymentInfo.description,
        metadata: deploymentInfo.metadata || {},
      }
    }),
  });

  if (!response.ok) {
    throw new Error(\`Failed to post deployment: \${response.statusText}\`);
  }

  return await response.json();
};

// Call on deployment success
postDeploymentToSmartSaaS({
  url: 'https://your-app.lovable.app',
  version: '1.0.0',
  environment: 'production',
  commitHash: process.env.COMMIT_HASH,
  branch: process.env.BRANCH,
  appName: 'My Lovable App',
  description: 'App description',
  metadata: {
    // Add any custom metadata
  }
});`;

  const webhookCodeExample = `// Webhook Configuration for Lovable
// Set this as a webhook URL in your Lovable project settings

// SmartSaaS will receive POST requests at:
// ${apiEndpoint}/webhooks/lovable

// Expected payload format:
{
  "event": "deployment.completed",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "deployment": {
      "url": "https://your-app.lovable.app",
      "version": "1.0.0",
      "environment": "production"
    },
    "app": {
      "name": "My Lovable App",
      "data": {
        // Your app data here
      }
    }
  }
}`;

  return (
    <ErrorBoundary>
      <ScrollView showsVerticalScrollIndicator={false}>
        <YStack gap="$4" paddingBottom="$4">
          {/* Header Card */}
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 12
                }}>
                  <MaterialCommunityIcons name="code-tags" size={32} color="#0ea5e9" />
                </View>
                <YStack flex={1}>
                  <H3
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Connect Lovable Deployments
                  </H3>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Automatically sync your Lovable app deployments and data to SmartSaaS
                  </SizableText>
                </YStack>
              </XStack>
            </Card>
          </Animatable.View>

          {/* Step 1: API Key Setup */}
          <Animatable.View animation="fadeInUp" duration={600} delay={100}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#fef3c7" : "#78350f",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons name="vpn-key" size={24} color="#f59e0b" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Step 1: Create API Key
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Generate a backend API key with data and integration permissions
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$3">
                <InstructionStep
                  number={1}
                  text="Navigate to Settings > API in SmartSaaS"
                  theme={theme}
                />
                <InstructionStep
                  number={2}
                  text="Click 'Create New' in the Backend API Keys section"
                  theme={theme}
                />
                <InstructionStep
                  number={3}
                  text="Select these permissions: data:write, integrations:write"
                  theme={theme}
                />
                <InstructionStep
                  number={4}
                  text="Copy your API key - you'll need it for the next step"
                  theme={theme}
                />

                <Button
                  backgroundColor="#5CC0EB"
                  color="white"
                  onPress={navigateToAPI}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                  marginTop="$2"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="settings" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Go to API Settings
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Step 2: Webhook Setup (Optional) */}
          <Animatable.View animation="fadeInUp" duration={600} delay={200}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialCommunityIcons name="webhook" size={24} color="#3b82f6" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Step 2: Set Up Webhook (Optional)
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Create a webhook to receive real-time deployment notifications
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$3">
                <InstructionStep
                  number={1}
                  text="In API Settings, go to Webhook Management section"
                  theme={theme}
                />
                <InstructionStep
                  number={2}
                  text="Click 'Create Webhook'"
                  theme={theme}
                />
                <InstructionStep
                  number={3}
                  text="Enter your Lovable webhook URL (if available)"
                  theme={theme}
                />
                <InstructionStep
                  number={4}
                  text="Select deployment events you want to track"
                  theme={theme}
                />

                <Button
                  backgroundColor="#3b82f6"
                  color="white"
                  onPress={navigateToAPI}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                  marginTop="$2"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialCommunityIcons name="webhook" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Create Webhook
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Step 3: Configure Lovable */}
          <Animatable.View animation="fadeInUp" duration={600} delay={300}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#dcfce7" : "#14532d",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialCommunityIcons name="code-braces" size={24} color="#10b981" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Step 3: Configure Lovable Deployment
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Add deployment hooks to your Lovable project
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$4">
                <YStack gap="$2">
                  <XStack alignItems="center" justifyContent="space-between">
                    <SizableText
                      size="$3"
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="600"
                    >
                      API Endpoint
                    </SizableText>
                    <Pressable
                      onPress={() => copyToClipboard(apiEndpoint, 'API Endpoint')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                        borderRadius: 6
                      }}
                    >
                      <MaterialIcons
                        name={copiedItem === 'API Endpoint' ? "check" : "content-copy"}
                        size={16}
                        color={copiedItem === 'API Endpoint' ? "#10b981" : (theme ? "#64748b" : "#94a3b8")}
                      />
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        {copiedItem === 'API Endpoint' ? 'Copied!' : 'Copy'}
                      </SizableText>
                    </Pressable>
                  </XStack>
                  <CodeBlock code={apiEndpoint} theme={theme} />
                </YStack>

                <YStack gap="$2">
                  <XStack alignItems="center" justifyContent="space-between">
                    <SizableText
                      size="$3"
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="600"
                    >
                      Deployment Code Example
                    </SizableText>
                    <Pressable
                      onPress={() => copyToClipboard(deploymentCodeExample, 'Deployment Code')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                        borderRadius: 6
                      }}
                    >
                      <MaterialIcons
                        name={copiedItem === 'Deployment Code' ? "check" : "content-copy"}
                        size={16}
                        color={copiedItem === 'Deployment Code' ? "#10b981" : (theme ? "#64748b" : "#94a3b8")}
                      />
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        {copiedItem === 'Deployment Code' ? 'Copied!' : 'Copy'}
                      </SizableText>
                    </Pressable>
                  </XStack>
                  <CodeBlock code={deploymentCodeExample} theme={theme} />
                </YStack>

                <YStack gap="$2">
                  <XStack alignItems="center" justifyContent="space-between">
                    <SizableText
                      size="$3"
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="600"
                    >
                      Webhook Payload Example
                    </SizableText>
                    <Pressable
                      onPress={() => copyToClipboard(webhookCodeExample, 'Webhook Code')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                        borderRadius: 6
                      }}
                    >
                      <MaterialIcons
                        name={copiedItem === 'Webhook Code' ? "check" : "content-copy"}
                        size={16}
                        color={copiedItem === 'Webhook Code' ? "#10b981" : (theme ? "#64748b" : "#94a3b8")}
                      />
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        {copiedItem === 'Webhook Code' ? 'Copied!' : 'Copy'}
                      </SizableText>
                    </Pressable>
                  </XStack>
                  <CodeBlock code={webhookCodeExample} theme={theme} />
                </YStack>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Data Format Examples */}
          <Animatable.View animation="fadeInUp" duration={600} delay={400}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#f3e8ff" : "#581c87",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons name="data-object" size={24} color="#8b5cf6" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Data Format Examples
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Example payloads for deployment and app data
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$4">
                <YStack gap="$2">
                  <SizableText
                    size="$3"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Deployment Metadata
                  </SizableText>
                  <CodeBlock
                    code={JSON.stringify({
                      deployment: {
                        url: "https://your-app.lovable.app",
                        version: "1.0.0",
                        timestamp: "2024-01-15T10:30:00Z",
                        environment: "production",
                        commitHash: "abc123",
                        branch: "main"
                      }
                    }, null, 2)}
                    theme={theme}
                  />
                </YStack>

                <YStack gap="$2">
                  <SizableText
                    size="$3"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    App Data
                  </SizableText>
                  <CodeBlock
                    code={JSON.stringify({
                      app: {
                        name: "My Lovable App",
                        description: "App description",
                        data: {
                          users: 100,
                          features: ["feature1", "feature2"],
                          customField: "value"
                        }
                      }
                    }, null, 2)}
                    theme={theme}
                  />
                </YStack>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Success Message */}
          <Animatable.View animation="fadeInUp" duration={600} delay={500}>
            <Card
              backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
              borderColor={theme ? "#dcfce7" : "#065f46"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#d1fae5" : "#065f46",
                  borderRadius: 50,
                  padding: 12
                }}>
                  <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                </View>
                <H4
                  color={theme ? "#059669" : "#10b981"}
                  fontWeight="600"
                  textAlign="center"
                >
                  Ready to Connect!
                </H4>
                <SizableText
                  size="$4"
                  color={theme ? "#065f46" : "#34d399"}
                  textAlign="center"
                >
                  Once configured, your Lovable deployments will automatically sync with SmartSaaS. Deployment metadata and app data will be posted on each successful deployment.
                </SizableText>
              </YStack>
            </Card>
          </Animatable.View>
        </YStack>
      </ScrollView>
    </ErrorBoundary>
  );
};

const InstructionStep = ({ number, text, theme }: { number: number; text: string; theme: boolean }) => (
  <XStack alignItems="flex-start" gap="$3">
    <View style={{
      backgroundColor: theme ? "#f3e8ff" : "#581c87",
      borderRadius: 20,
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <SizableText
        size="$3"
        color="#8b5cf6"
        fontWeight="700"
      >
        {number}
      </SizableText>
    </View>
    <SizableText
      size="$4"
      color={theme ? "#1e293b" : "#f1f5f9"}
      flex={1}
      lineHeight={24}
    >
      {text}
    </SizableText>
  </XStack>
);

const CodeBlock = ({ code, theme }: { code: string; theme: boolean }) => (
  <View style={{
    backgroundColor: theme ? "#1e293b" : "#0f172a",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: theme ? "#334155" : "#1e293b"
  }}>
    <SizableText
      size="$2"
      color="#e2e8f0"
      fontFamily={Platform.OS === 'ios' ? 'Menlo' : 'monospace'}
      style={{
        fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
      }}
    >
      {code}
    </SizableText>
  </View>
);

export default LovableOptions;
















